import { useCallback, useEffect, useRef } from 'react';

interface IIdentifiableRequest {
	id: string;
}

const getDefaultRequestId = (request: IIdentifiableRequest): string =>
	request.id;

const useVisibleRequestIds = <TRequest extends IIdentifiableRequest>(
	requests: readonly TRequest[],
	onVisibleRequestIds: (requestIds: string[]) => Promise<void>,
	getRequestId: (request: TRequest) => string = getDefaultRequestId,
) => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const rowElements = useRef(new Map<string, HTMLDivElement>());
	const viewedRequestIds = useRef(new Set<string>());
	const pendingRequestIds = useRef(new Set<string>());

	const getRowRef = useCallback(
		(requestId: string) => (element: HTMLDivElement | null) => {
			if (element) {
				rowElements.current.set(requestId, element);
			} else {
				rowElements.current.delete(requestId);
			}
		},
		[],
	);

	const markVisibleRequestsViewed = useCallback(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		const scrollContainerRect = scrollContainer.getBoundingClientRect();
		const visibleRequestIds = requests
			.filter((request) => {
				const rowRect = rowElements.current
					.get(getRequestId(request))
					?.getBoundingClientRect();

				return Boolean(
					rowRect &&
						rowRect.bottom > scrollContainerRect.top &&
						rowRect.top < scrollContainerRect.bottom,
				);
			})
			.map(getRequestId)
			.filter(
				(requestId) =>
					!viewedRequestIds.current.has(requestId) &&
					!pendingRequestIds.current.has(requestId),
			);

		if (visibleRequestIds.length === 0) return;

		visibleRequestIds.forEach((requestId) => {
			pendingRequestIds.current.add(requestId);
		});
		void onVisibleRequestIds(visibleRequestIds)
			.then(() => {
				visibleRequestIds.forEach((requestId) => {
					pendingRequestIds.current.delete(requestId);
					viewedRequestIds.current.add(requestId);
				});
			})
			.catch(() => {
				visibleRequestIds.forEach((requestId) => {
					pendingRequestIds.current.delete(requestId);
				});
			});
	}, [getRequestId, onVisibleRequestIds, requests]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) return;

		const animationFrame = requestAnimationFrame(markVisibleRequestsViewed);
		scrollContainer.addEventListener('scroll', markVisibleRequestsViewed);

		return () => {
			cancelAnimationFrame(animationFrame);
			scrollContainer.removeEventListener('scroll', markVisibleRequestsViewed);
		};
	}, [markVisibleRequestsViewed]);

	return { getRowRef, scrollContainerRef };
};

export { useVisibleRequestIds };
